import dlv from 'dlv';
import { FieldValues } from 'react-hook-form';
import { FieldGroupCreateRequest, FieldValuesMap } from '../../../types/policy';
import { PremiumReportUpdateRequest } from '../../../types/premiumReports';
import {
	mergeWithRemovedFieldGroups,
	mergeWithUpdateFieldGroups,
	removeEmptyFieldGroups,
	removeEmptyFieldValuesFromRequest,
} from '../../premiumReports/util';
import { FormShape } from '../types';
import { FormBuilder } from '../types/FormBuilder';
import { getPathsForForms, updateRequestBuilder } from '../utils';

export type UpdateFieldGroup = {
	fieldName: string;
	fieldGroupLocator: string;
	fieldValues: FieldValuesMap;
};

export type PremiumReportUpdateObject = {
	fieldValues: FieldValuesMap;
	addFieldGroups?: FieldGroupCreateRequest[];
	updateFieldGroups?: UpdateFieldGroup[];
	fieldGroups?: FieldGroupCreateRequest[];
	removeFieldGroups?: string[];
};

// submittedUpdateObject contains any pending changes and the original values of premium report
const premiumReportUpdateBuilder = (
	dirtyFields: { [key: string]: boolean },
	formValues: FieldValues,
	fields: FormShape<FormBuilder>,
	primaryFormName: string,
	timezone = 'America/Los_Angeles',
	currUpdateObj: PremiumReportUpdateRequest,
) => {
	const paths = getPathsForForms(fields, primaryFormName);
	let updateObject: PremiumReportUpdateObject = {
		fieldValues: { ...(currUpdateObj.fieldValues || {}) },
		fieldGroups: [...(currUpdateObj.fieldGroups || [])],
	};

	paths.forEach((key) => {
		if (Object.keys(dlv(dirtyFields, key, {})).length > 0) {
			let newUpdatedObject: PremiumReportUpdateObject;
			const currDirtyFields = { [key]: dlv(dirtyFields, key, {}) };
			const currFormValues = { [key]: dlv(formValues, key, {}) };
			const currFields = {
				[key]: dlv(fields, key, {}),
			} as FormShape<FormBuilder>;

			if (primaryFormName === key) {
				newUpdatedObject = updateRequestBuilder(
					currDirtyFields,
					currFormValues,
					currFields,
					key,
					timezone,
				);

				updateObject = {
					fieldValues: {
						...(currUpdateObj.fieldValues || {}),
						...newUpdatedObject.fieldValues,
					},
					fieldGroups: [
						...(currUpdateObj.fieldGroups || []),
						...(newUpdatedObject.addFieldGroups || []),
					],
					updateFieldGroups: [...(newUpdatedObject.updateFieldGroups || [])],
					removeFieldGroups: [...(newUpdatedObject.removeFieldGroups || [])],
				};
			}
		}
	});

	if (updateObject?.updateFieldGroups?.length) {
		updateObject = mergeWithUpdateFieldGroups(
			updateObject,
			updateObject.updateFieldGroups,
		);
	}

	if (updateObject?.removeFieldGroups?.length) {
		updateObject = mergeWithRemovedFieldGroups(
			updateObject,
			updateObject.removeFieldGroups,
		);
	}

	if (updateObject?.fieldGroups?.length) {
		updateObject = removeEmptyFieldGroups(updateObject);
	}

	const submittedUpdateObject: PremiumReportUpdateRequest =
		removeEmptyFieldValuesFromRequest({
			...currUpdateObj,
			fieldValues: updateObject.fieldValues || {},
			fieldGroups: updateObject.fieldGroups || [],
		});

	return submittedUpdateObject;
};

export default premiumReportUpdateBuilder;
